import { useState, useEffect } from "react";
import { Calendar, Plus, Minus } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/currencyFormatter";
import { MenuItem } from "@/types/menu";

// Define form schema using Zod
const inventorySchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  category: z.string().min(1, { message: "Category is required" }),
  initialStock: z.coerce
    .number()
    .nonnegative({ message: "Initial stock must be a positive number" }),
  unit: z.string().min(1, { message: "Unit is required" }),
  minStock: z.coerce
    .number()
    .nonnegative({ message: "Minimum stock must be a positive number" }),
  reorderPoint: z.coerce
    .number()
    .nonnegative({ message: "Reorder point must be a positive number" }),
  supplierName: z.string().optional(),
  description: z.string().optional(),
  expiryDate: z.date().optional(),
  location: z.string().optional(),
});

const manualInventorySchema = z.object({
  name: z.string().optional(),
  quantity: z.coerce
    .number()
    .nonnegative({ message: "Quantity must be a positive number" }),
  menuItem: z.string().optional(),
  linkToMenu: z.boolean().default(false),
  alwaysAvailable: z.boolean().default(true),
  availabilityFrom: z.date().optional(),
  availabilityTo: z.date().optional(),
});

type InventoryFormValues = z.infer<typeof inventorySchema>;
type ManualInventoryFormValues = z.infer<typeof manualInventorySchema>;

interface InventoryManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  initialData?: InventoryFormValues;
  onInventoryAdded?: () => void;
  isAdvancedMode?: boolean;
  refreshInventory?: () => void;
}

export function InventoryManagementDialog({
  open,
  onOpenChange,
  storeId,
  initialData,
  onInventoryAdded,
  isAdvancedMode = true,
  refreshInventory,
}: InventoryManagementDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [activeTab, setActiveTab] = useState<string>("advanced");
  const [linkedMenuItems, setLinkedMenuItems] = useState<Set<string>>(
    new Set()
  );

  // Sample categories
  const categories = [
    "Meat",
    "Dairy",
    "Frozen",
    "Produce",
    "Bakery",
    "Beverages",
    "Condiments",
    "Spices",
    "Dry Goods",
    "Cleaning Supplies",
  ];

  // Sample units
  const units = [
    "kg",
    "g",
    "lbs",
    "oz",
    "l",
    "ml",
    "pcs",
    "boxes",
    "cans",
    "bottles",
    "packages",
  ];

  // Set up form with default values for advanced inventory
  const advancedForm = useForm<InventoryFormValues>({
    resolver: zodResolver(inventorySchema),
    defaultValues: initialData || {
      name: "",
      category: "",
      initialStock: undefined,
      unit: "pcs",
      minStock: undefined,
      reorderPoint: undefined,
      supplierName: "",
      description: "",
      location: "",
    },
  });

  // Set up form for manual inventory
  const manualForm = useForm<ManualInventoryFormValues>({
    resolver: zodResolver(manualInventorySchema),
    defaultValues: {
      name: "",
      quantity: 1,
      menuItem: "",
      linkToMenu: false,
      alwaysAvailable: true,
      availabilityFrom: undefined,
      availabilityTo: undefined,
    },
  });

  // Fetch menu items for the manual inventory form
  useEffect(() => {
    if (storeId && open && !isAdvancedMode) {
      fetchMenuItems();
    }
  }, [storeId, open, isAdvancedMode]);

  // Set the active tab based on advanced mode
  useEffect(() => {
    setActiveTab(isAdvancedMode ? "advanced" : "manual");
  }, [isAdvancedMode]);

  // Add this effect to fetch linked menu items when the dialog opens
  useEffect(() => {
    const fetchLinkedMenuItems = async () => {
      if (!storeId) return;

      const { data, error } = await supabase
        .from("menu_item_inventory")
        .select("menu_item_id, inventory_items!inner(inventory_type)")
        .eq("inventory_items.inventory_type", "manual");

      if (!error && data) {
        setLinkedMenuItems(new Set(data.map((item) => item.menu_item_id)));
      }
    };

    fetchLinkedMenuItems();
  }, [storeId]);

  const fetchMenuItems = async () => {
    try {
      const { data, error } = await supabase
        .from("menu_items")
        .select("id, name, price, description, available, store_id")
        .eq("store_id", storeId)
        .order("name");

      if (error) throw error;

      setMenuItems(data || []);
    } catch (error) {
      console.error("Error fetching menu items:", error);
      toast({
        title: "Error",
        description: "Failed to load menu items.",
        variant: "destructive",
      });
    }
  };

  // Handle submission for advanced inventory form
  const onSubmitAdvanced = async (data: InventoryFormValues) => {
    if (!storeId) {
      toast({
        title: "Error",
        description: "No store selected. Please select a store first.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Save the inventory item to the database
      const { data: newInventoryItem, error } = await supabase
        .from("inventory_items")
        .insert({
          name: data.name,
          description: data.description,
          category: data.category,
          store_id: storeId,
          current_quantity: data.initialStock,
          initial_stock: data.initialStock,
          minimum_quantity: data.minStock,
          reorder_point: data.reorderPoint,
          supplier_name: data.supplierName,
          unit_of_measure: data.unit,
          storage_location: data.location,
          expiry_date: data.expiryDate
            ? data.expiryDate.toISOString().split("T")[0]
            : null, // Convert Date to string
          inventory_type: "advanced",
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating inventory item:", error);
        throw error;
      }
      // Show success toast
      toast({
        title: "Inventory item created",
        description: `${data.name} has been added to your inventory.`,
      });

      // Close dialog
      onOpenChange(false);

      // Trigger refresh of inventory items
      if (onInventoryAdded) {
        onInventoryAdded();
      }
    } catch (error) {
      console.error("Error creating inventory item:", error);
      toast({
        title: "Error",
        description: "Failed to create inventory item. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle submission for manual inventory form
  const onSubmitManual = async (data: ManualInventoryFormValues) => {
    if (!storeId) {
      toast({
        title: "Error",
        description: "No store selected. Please select a store first.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // If linking to menu item, check if it's already linked
      if (data.linkToMenu && data.menuItem) {
        // Check for existing relationship
        const { data: existingRelation, error: checkError } = await supabase
          .from("menu_item_inventory")
          .select("id, inventory_items!inner(inventory_type)")
          .eq("menu_item_id", data.menuItem)
          .eq("inventory_items.inventory_type", "manual")
          .maybeSingle();

        if (checkError) {
          throw checkError;
        }

        if (existingRelation) {
          toast({
            title: "Error",
            description:
              "This menu item is already linked to a manual inventory item.",
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Get menu item data if linked
      let menuItem = null;
      let relatedMenuItemId = null;

      if (data.linkToMenu && data.menuItem) {
        const { data: menuData, error: menuError } = await supabase
          .from("menu_items")
          .select("*")
          .eq("id", data.menuItem)
          .single();

        if (menuError) {
          console.error("Error fetching menu item:", menuError);
        } else {
          menuItem = menuData;
          relatedMenuItemId = menuData.id;
        }
      }

      // Format dates for database
      const availabilityFrom = data.availabilityFrom
        ? data.availabilityFrom.toISOString().split("T")[0]
        : null;

      const availabilityTo = data.availabilityTo
        ? data.availabilityTo.toISOString().split("T")[0]
        : null;

      // Create inventory item
      const { data: newInventoryItem, error } = await supabase
        .from("inventory_items")
        .insert({
          name:
            data.linkToMenu && menuItem
              ? `${menuItem.name} (Manual Inventory)`
              : data.name,
          store_id: storeId,
          current_quantity: data.quantity,
          inventory_type: "manual",
          related_menu_item_id: relatedMenuItemId,
          is_menu_linked: data.linkToMenu,
          always_available: data.alwaysAvailable,
          availability_from: availabilityFrom,
          availability_to: availabilityTo,
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating manual inventory item:", error);
        throw error;
      }


      // If linked to menu item, create relationship
      if (data.linkToMenu && relatedMenuItemId) {
        const { error: relError } = await supabase
          .from("menu_item_inventory")
          .insert({
            menu_item_id: relatedMenuItemId,
            inventory_item_id: newInventoryItem.id,
            quantity_required: 1, // Fixed quantity for manual inventory
          });

        if (relError) {
          console.error(
            "Error creating menu-inventory relationship:",
            relError
          );
          // Continue anyway since the inventory item was created
        }
      }

      // Show success toast
      toast({
        title: "Manual inventory item created",
        description: `${newInventoryItem.name} has been added to your inventory.`,
      });
      if (refreshInventory) {
        refreshInventory();
      }
      onOpenChange(false);

      // Trigger refresh of inventory items
      if (onInventoryAdded) {
        onInventoryAdded();
      }
    } catch (error) {
      console.error("Error creating manual inventory item:", error);
      toast({
        title: "Error",
        description: "Failed to create inventory item. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Watch for alwaysAvailable value changes to enable/disable date fields
  const alwaysAvailable = manualForm.watch("alwaysAvailable");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] w-[95vw] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-xl">
            Add Inventory Item
          </DialogTitle>
          <DialogDescription className="text-sm">
            Fill out the details below to add a new item to your inventory.
          </DialogDescription>
        </DialogHeader>

        {isAdvancedMode ? (
          // Advanced Inventory Form
          <Form {...advancedForm}>
            <form
              onSubmit={advancedForm.handleSubmit(onSubmitAdvanced)}
              className="space-y-4 py-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={advancedForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Item Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter item name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={advancedForm.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <FormField
                  control={advancedForm.control}
                  name="initialStock"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Initial Stock</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          value={field.value === undefined ? "" : field.value}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ""
                                ? undefined
                                : Number(e.target.value)
                            )
                          }
                          placeholder="0"
                          min="0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={advancedForm.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select unit" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {units.map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={advancedForm.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Storage Location</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., Kitchen, Freezer 1"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={advancedForm.control}
                  name="minStock"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum Stock</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          value={field.value === undefined ? "" : field.value}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ""
                                ? undefined
                                : Number(e.target.value)
                            )
                          }
                          placeholder="0"
                          min="0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={advancedForm.control}
                  name="reorderPoint"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reorder Point</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          value={field.value === undefined ? "" : field.value}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ""
                                ? undefined
                                : Number(e.target.value)
                            )
                          }
                          placeholder="0"
                          min="0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={advancedForm.control}
                name="expiryDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Expiry Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={`w-full justify-start text-left font-normal ${
                              !field.value && "text-muted-foreground"
                            }`}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {field.value
                              ? format(field.value, "PPP")
                              : "Select date"}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={advancedForm.control}
                  name="supplierName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Supplier Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter supplier name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={advancedForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Additional details about this inventory item"
                        className="resize-none"
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="mr-2"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : "Save Item"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        ) : (
          // Manual Inventory Form
          <Form {...manualForm}>
            <form
              onSubmit={manualForm.handleSubmit(onSubmitManual)}
              className="space-y-4 py-4"
            >
              <FormField
                control={manualForm.control}
                name="linkToMenu"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Link to Menu Item</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Connect this inventory to an existing menu item
                      </p>
                    </div>
                  </FormItem>
                )}
              />

              {manualForm.watch("linkToMenu") ? (
                <FormField
                  control={manualForm.control}
                  name="menuItem"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Select Menu Item</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose menu item" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="z-[100] bg-background">
                          {menuItems.map((item) => (
                            <SelectItem
                              key={item.id}
                              value={item.id}
                              disabled={linkedMenuItems.has(item.id)}
                              className={
                                linkedMenuItems.has(item.id) ? "opacity-50" : ""
                              }
                            >
                              <span className="flex items-center gap-2">
                                {item.name} (${Number(item.price).toFixed(2)})
                                {linkedMenuItems.has(item.id) && (
                                  <span className="text-xs text-muted-foreground">
                                    (Already linked)
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <FormField
                  control={manualForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Item Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter item name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={manualForm.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Available Quantity</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value === undefined ? "1" : field.value}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === "" ? 1 : Number(e.target.value)
                          )
                        }
                        placeholder="1"
                        min="0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Always Available Toggle */}
              <FormField
                control={manualForm.control}
                name="alwaysAvailable"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Always Available</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Item is available at all times
                      </p>
                    </div>
                  </FormItem>
                )}
              />

              {/* Availability Dates (shown only when Always Available is off) */}
              {!alwaysAvailable && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Availability From Date */}
                  <FormField
                    control={manualForm.control}
                    name="availabilityFrom"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Available From</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                <Calendar className="mr-2 h-4 w-4" />
                                {field.value
                                  ? format(field.value, "PPP")
                                  : "Select start date"}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              initialFocus
                              className="p-3 pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Availability To Date */}
                  <FormField
                    control={manualForm.control}
                    name="availabilityTo"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Available Until</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                <Calendar className="mr-2 h-4 w-4" />
                                {field.value
                                  ? format(field.value, "PPP")
                                  : "Select end date"}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              initialFocus
                              className="p-3 pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="mr-2"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : "Save Item"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
